import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { api, SESSION_EXPIRED_EVENT } from '@/lib/api'
import {
  clearSession,
  getIdentity,
  getToken,
  isIdleLocked,
  setSession,
  touchActivity,
  type AdminIdentity,
} from '@/lib/auth'
import { can, type Permission } from '@/lib/permissions'
import { fetchMe } from '@/lib/adapters'

/** A1: sessiya holati — ilova nima ko'rsatishini shu belgilaydi. */
export type SessionState =
  | 'loading'
  | 'anonymous' // A1.01 — kirish ekrani
  | 'authenticated'
  | 'expired' // A1.05 — sessiya tugadi
  | 'locked' // A1 — 30 daqiqa faoliyatsizlik

interface AuthValue {
  state: SessionState
  identity: AdminIdentity | null
  signIn: (username: string, password: string, totp?: string) => Promise<void>
  signOut: () => void
  /** Qulfni ochish — parol qayta so'raladi (A1). */
  unlock: (password: string) => Promise<void>
  can: (permission: Permission) => boolean
}

const AuthContext = createContext<AuthValue | null>(null)

interface LoginResponse {
  token: string
  admin: { username: string; role: string; roles?: string[] }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>('loading')
  const [identity, setIdentity] = useState<AdminIdentity | null>(null)

  // Ilk yuklanish: saqlangan sessiyani tiklaymiz.
  useEffect(() => {
    const token = getToken()
    const id = getIdentity()
    if (!token || !id) {
      setState('anonymous')
      return
    }
    setIdentity(id)
    setState(isIdleLocked() ? 'locked' : 'authenticated')
  }, [])

  // P1 RBAC: autentifikatsiyadan keyin `GET /admin/me` huquqlarni beradi va ular
  // klient rol→huquq ko'prigidan ustun turadi (server = manba). Endpoint yo'q/xato
  // bo'lsa — jimgina ko'prikда qolamiz (buzilmaydi).
  useEffect(() => {
    if (state !== 'authenticated') return
    let alive = true
    fetchMe()
      .then((me) => {
        if (!alive || !me) return
        setIdentity((prev) => {
          if (!prev) return prev
          const next: AdminIdentity = {
            ...prev,
            role: me.role || prev.role,
            ...(me.roles?.length ? { roles: me.roles } : {}),
            ...(me.permissions ? { permissions: me.permissions } : {}),
          }
          const tok = getToken()
          if (tok) setSession(tok, next) // reload'da server-huquqlar saqlansin
          return next
        })
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [state])

  // Server 401 bersa — butun ilova bir joyda xabardor bo'ladi (api.ts).
  useEffect(() => {
    function onExpired() {
      setState((prev) => (prev === 'anonymous' ? prev : 'expired'))
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired)
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired)
  }, [])

  // Faollik kuzatuvi + 30 daqiqada qulflash (TZ A1).
  const stateRef = useRef(state)
  stateRef.current = state
  useEffect(() => {
    if (state !== 'authenticated') return

    const onActivity = () => touchActivity()
    const events = ['pointerdown', 'keydown', 'wheel'] as const
    for (const e of events) window.addEventListener(e, onActivity, { passive: true })

    // Har 30 soniyada tekshiramiz — taymer emas, chunki qurilma uxlab
    // qolsa taymer kechikadi, sana-farqi esa aniq qoladi.
    const iv = window.setInterval(() => {
      if (stateRef.current === 'authenticated' && isIdleLocked()) setState('locked')
    }, 30_000)

    return () => {
      for (const e of events) window.removeEventListener(e, onActivity)
      window.clearInterval(iv)
    }
  }, [state])

  const signIn = useCallback(async (username: string, password: string, totp?: string) => {
    // A28.02 — totp_code only travels when the account has 2FA on (the server
    // returns TWO_FACTOR_REQUIRED otherwise, and the login screen re-prompts).
    const data = await api.post<LoginResponse>('/admin/login', { username, password, totp_code: totp })
    const id: AdminIdentity = {
      username: data.admin.username,
      role: data.admin.role,
      ...(data.admin.roles ? { roles: data.admin.roles } : {}),
    }
    setSession(data.token, id)
    setIdentity(id)
    setState('authenticated')
  }, [])

  const signOut = useCallback(() => {
    clearSession()
    setIdentity(null)
    setState('anonymous')
  }, [])

  const unlock = useCallback(
    async (password: string) => {
      const username = identity?.username
      if (!username) {
        signOut()
        return
      }
      // Qulfni ochish = qayta autentifikatsiya (yangi token ham olamiz).
      await signIn(username, password)
    },
    [identity, signIn, signOut],
  )

  const value = useMemo<AuthValue>(
    () => ({
      state,
      identity,
      signIn,
      signOut,
      unlock,
      can: (p: Permission) => can(identity, p),
    }),
    [state, identity, signIn, signOut, unlock],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}

/**
 * rbac-ui.md: huquqi yo'q element RENDER QILINMAYDI (disabled emas —
 * disabled tugma ham ma'lumot sizdiradi).
 *
 * Istisno: `mode="disabled"` — mavjudligini bilish foydali bo'lganda
 * (xodim huquq so'rashi mumkin).
 */
export function PermissionGate({
  permission,
  mode = 'hide',
  hint,
  children,
  fallback = null,
}: {
  permission: Permission
  mode?: 'hide' | 'disabled'
  hint?: string
  children: ReactNode
  fallback?: ReactNode
}) {
  const { can: allowed } = useAuth()
  if (allowed(permission)) return <>{children}</>
  if (mode === 'hide') return <>{fallback}</>
  return (
    <span
      title={hint ?? permission}
      aria-disabled="true"
      className="pointer-events-none inline-flex opacity-45"
    >
      {children}
    </span>
  )
}
